import type { PlanIntro } from "./plan-intro";
import {
  annualSavingsPercent,
  planIntroOffer,
  planIntroTerm,
  planTrialOffer,
} from "./plan-intro-runtime";

/**
 * A plan's tier card, as the Membership page, Home's offers, a page's Memberships element,
 * sign-in and the member apps show it: what a public listing adds to a plan (members, places
 * left, a yearly saving), and the words its card uses for them.
 */

/** A monthly plan and its yearly twin, and how much paying yearly saves. Both plans carry it. */
export type AnnualSavings = { percent: number; monthlyPlanId: string; yearlyPlanId: string };

type TierPlan = {
  id: string;
  title: string;
  currency: string;
  interval: string;
  priceCents: number;
  manualOnly?: boolean;
  memberLimit?: number;
  showMemberCount?: boolean;
};

/** What a public listing adds to each plan (publicPlanCards). */
export type PublicPlanExtras = {
  /** Places left on a plan with a member limit. */
  remaining?: number;
  /** Members of the plan's tier, on a plan that shows them. */
  memberCount?: number;
  annualSavings?: AnnualSavings;
};

/** Plans with the same title and currency are one tier, billed in different ways. */
function planTierKey(plan: { title: string; currency: string }) {
  return `${plan.title.trim().toLocaleLowerCase()}:${plan.currency}`;
}

/** Each monthly plan and its yearly twin, by both plans' ids, when paying yearly saves 1% or more. */
export function annualSavingsPairs(plans: readonly TierPlan[]) {
  const monthly = new Map<string, TierPlan>();
  const yearly = new Map<string, TierPlan>();
  for (const plan of plans) {
    const tier = plan.interval === "month" ? monthly : plan.interval === "year" ? yearly : null;
    if (tier && !tier.has(planTierKey(plan))) tier.set(planTierKey(plan), plan);
  }
  const pairs = new Map<string, AnnualSavings>();
  for (const [key, month] of monthly) {
    const year = yearly.get(key);
    const percent = year ? annualSavingsPercent(month.priceCents, year.priceCents) : null;
    if (!year || !percent) continue;
    const savings = { percent, monthlyPlanId: month.id, yearlyPlanId: year.id };
    pairs.set(month.id, savings).set(year.id, savings);
  }
  return pairs;
}

/**
 * The plans a public listing counts members on: a limited plan, for the places it has left, and
 * every plan of a tier whose card shows its members. `all` is every plan the creator has, so
 * members still on an archived price of the tier count too; manual access never does.
 */
export function plansToCount(listed: readonly TierPlan[], all: readonly TierPlan[]) {
  const tiers = new Set(listed.filter((plan) => plan.showMemberCount).map(planTierKey));
  return [
    ...new Set([
      ...listed.filter((plan) => plan.memberLimit).map((plan) => plan.id),
      ...all
        .filter((plan) => !plan.manualOnly && tiers.has(planTierKey(plan)))
        .map((plan) => plan.id),
    ]),
  ];
}

/**
 * A public listing's plans with what their cards show beyond the plan itself: the places a
 * limited plan has left, the members of a tier that shows them (the sum of its plans' members),
 * and a monthly and yearly pair's saving. `counts` holds the members of each plan plansToCount
 * named.
 */
export function publicPlanCards<P extends TierPlan>(
  listed: readonly P[],
  all: readonly TierPlan[],
  counts: ReadonlyMap<string, number>,
): (P & PublicPlanExtras)[] {
  const savings = annualSavingsPairs(listed);
  return listed.map((plan) => {
    const tier = planTierKey(plan);
    const extras: PublicPlanExtras = {};
    if (plan.memberLimit)
      extras.remaining = Math.max(0, plan.memberLimit - (counts.get(plan.id) ?? 0));
    if (plan.showMemberCount)
      extras.memberCount = all
        .filter((other) => !other.manualOnly && planTierKey(other) === tier)
        .reduce((sum, other) => sum + (counts.get(other.id) ?? 0), 0);
    const pair = savings.get(plan.id);
    if (pair) extras.annualSavings = pair;
    return { ...plan, ...extras };
  });
}

/** A plan's own perks: its benefits, trimmed, without empty ones. */
export function planOwnPerks(plan: { benefits?: readonly string[] | null }) {
  return (plan.benefits ?? []).map((perk) => perk.trim()).filter(Boolean);
}

/**
 * A plan's description where only the description is shown, as in the member apps and the Drate
 * app's offers: its own, or, on a plan with perks and no description, its perks one per line.
 */
export function planDescriptionOrPerks(plan: {
  description?: string | null;
  benefits?: readonly string[] | null;
}) {
  const description = plan.description ?? "";
  const perks = planOwnPerks(plan);
  return description.trim() || !perks.length ? description : perks.join("\n");
}

/** "51 members", or null when a card doesn't show its members or has none yet. */
export function planMembersLabel(count: number | undefined) {
  return count ? `${count.toLocaleString("en-US")} ${count === 1 ? "member" : "members"}` : null;
}

/**
 * A plan's first-time offer as its card says it: an intro, "$1" for the "first month", or a free
 * trial, "Free" for "7 days", then the plan's own price ("then $5/month"). Null for a plan
 * without one that applies. `price` formats an amount the way the surface prices plans.
 */
export function planIntroLabel(
  plan: {
    priceCents: number;
    currency: string;
    interval: string;
    intro?: PlanIntro | null;
    trialDays?: number | null;
  },
  price: (cents: number, currency: string) => string,
) {
  const after = `then ${price(plan.priceCents, plan.currency)}/${plan.interval === "year" ? "year" : "month"}`;
  const days = planTrialOffer(plan);
  if (days) return { price: "Free", term: `for ${days} ${days === 1 ? "day" : "days"}`, after };
  const intro = planIntroOffer(plan);
  if (!intro) return null;
  return { price: price(intro.priceCents, plan.currency), term: planIntroTerm(plan, intro), after };
}

/**
 * What a card says about paying yearly: on the monthly plan, "Save 10% if you pay annually",
 * with the yearly plan to join; on the yearly plan, what it saves against paying monthly.
 */
export function planSavingsLabel(plan: { id: string; annualSavings?: AnnualSavings }) {
  const savings = plan.annualSavings;
  if (!savings) return null;
  return plan.id === savings.monthlyPlanId
    ? { text: `Save ${savings.percent}% if you pay annually`, planId: savings.yearlyPlanId }
    : { text: `Save ${savings.percent}% compared with paying monthly`, planId: null };
}
